import {
  isWidgetUploadedAttachment,
  type WidgetAttachment,
  type WidgetUploadingAttachment,
  type WidgetUploadingAttachmentError,
} from '@widgets/shared/types'
import messengerApiService from '@widgets/messenger/services/messengerApiService'
import translateService from '@widgets/shared/services/translate/translateService'
import sentryMonitoringService from '@widgets/shared/services/monitoring/sentryMonitoringService'
import loggerService from '@widgets/shared/services/loggerService'
import {
  messengerAcceptedFileExtensions,
  messengerMaxFilesCountPerMessage,
  messengerMaxFileSize,
} from '@widgets/messenger/const'
import { useWidgetAlerts } from '@widgets/messenger/compositions/useWidgetAlerts'
import type { MessengerAlert, MessengerAlertScope } from '@widgets/messenger/types/alert'
import messengerMessageListItemCastService from '@widgets/messenger/services/messengerMessageListItemCastService'
import type { AxiosPromise } from 'axios'
import type { MessengerApiAttachment } from '@widgets/messenger/types/api/common'
import type { AttachmentStore } from '@widgets/messenger/stores/useAttachmentStoreFactory'
import { getFileSizeLabel } from '@/utils/file/getFileSizeLabel'
import { getUuid } from '@/utils/uuid'
import { isImageMime } from '@/utils/file/mime'
import type { ICancelable } from '@/services/types'

export type UploadApiCallback = (payload: {
  file: File
  id: string
}) => AxiosPromise<MessengerApiAttachment> & ICancelable

interface UploadAttachmentParams {
  uploadApiCallback: UploadApiCallback
  file: File
  id?: string // used for retry
}

interface UploadAttachmentsParams {
  uploadApiCallback: UploadApiCallback
  files: File[]
}

interface RetryAttachmentParams {
  uploadApiCallback: UploadApiCallback
  id: string
}

type AttachmentAlertPayload = {
  attachmentId: string
}

const isAttachmentAlert = (alert: MessengerAlert): alert is MessengerAlert<AttachmentAlertPayload> =>
  alert.payload && 'attachmentId' in alert.payload

export const useWidgetAttachments = (attachmentStore: AttachmentStore, alertScope: MessengerAlertScope) => {
  const { addAlert, removeAlertBy } = useWidgetAlerts([alertScope])

  const addAttachmentAlert = (attachmentId: string, text: string) => {
    addAlert({
      text,
      payload: {
        attachmentId,
      } satisfies AttachmentAlertPayload,
      scope: alertScope,
    })
  }

  const removeAttachmentAlert = (attachmentId: string) => {
    removeAlertBy((alert) => isAttachmentAlert(alert) && alert.payload.attachmentId === attachmentId)
  }

  const setErrorForAttachment = (id: string, error: WidgetUploadingAttachmentError, showAlert = true) => {
    attachmentStore.setErrorForAttachment(id, error)
    if (showAlert) {
      addAttachmentAlert(id, error.text)
    }
  }

  const uploadAttachment = async ({ file, id, uploadApiCallback }: UploadAttachmentParams) => {
    const uploadingAttachment = getUploadingAttachmentFromFile(file, id)
    attachmentStore.insertOrUpdateUploadingAttachment(uploadingAttachment, { file })

    const promise = uploadApiCallback({ file, id: uploadingAttachment.id })

    attachmentStore.insertOrUpdateAttachmentUploadData(uploadingAttachment.id, { promise })

    try {
      const { data } = await promise
      const uploadedAttachment = messengerMessageListItemCastService.castApiAttachment(data)
      attachmentStore.markAttachmentAsUploaded(uploadingAttachment.id, uploadedAttachment)
    } catch (e) {
      if (messengerApiService.isCancel(e)) return
      if (messengerApiService.isApiError(e)) {
        const notRetryableErrorCodes = ['file.wrong_mime_type', 'file.too_big_file_size']
        const fieldErrorText = (e.response?.data?.errors?.fields?.file || []).join(' ') as string
        const commonErrorText = e.response?.data?.errors?.common || e.response?.data?.message
        const text = fieldErrorText || commonErrorText || translateService.t('common.somethingWentWrong')
        const canRetry = !notRetryableErrorCodes.includes(e.response?.data?.errorCode)
        const errorCode = e.response?.data?.errorCode
        // Don't show alert for MIME type and file size errors - they will be shown in tooltip
        const showAlert = !notRetryableErrorCodes.includes(errorCode)
        setErrorForAttachment(uploadingAttachment.id, { text, canRetry }, showAlert)
        return
      }

      setErrorForAttachment(uploadingAttachment.id, {
        text: translateService.t('common.somethingWentWrong'),
        canRetry: false,
      })
      sentryMonitoringService.logError(e as Error)
      loggerService.logError((e as Error).message)
    }
  }

  const validateAndFilterFiles = (files: File[]) => {
    const alreadyAttachedFilesCount = attachmentStore.attachments.value.length
    const allowedToAttachMoreFilesCount = messengerMaxFilesCountPerMessage - alreadyAttachedFilesCount
    const validFiles: File[] = []
    const filesWithErrors: Array<{ file: File; errorDescription: string }> = []

    files.forEach((file) => {
      let errorDescription: string | null = null

      if (file.size > messengerMaxFileSize) {
        errorDescription = translateService.tc('common.fileSizeError', 1, {
          size: getFileSizeLabel(messengerMaxFileSize),
        })
      }

      if (!errorDescription) {
        const ext = file.name.split('.').pop() || ''
        if (!messengerAcceptedFileExtensions.includes(`.${ext}`)) {
          errorDescription = translateService.tc('common.fileFormatError', 1)
        }
      }

      if (errorDescription) {
        filesWithErrors.push({ file, errorDescription })
      } else {
        validFiles.push(file)
      }
    })

    // Create attachments with error state for invalid files
    filesWithErrors.forEach(({ file, errorDescription }) => {
      const uploadingAttachment = getUploadingAttachmentFromFile(file)
      uploadingAttachment.error = {
        text: errorDescription,
        canRetry: false,
      }
      attachmentStore.insertOrUpdateUploadingAttachment(uploadingAttachment, { file })
    })

    // Always show file count limit error as alert (yellow bar)
    const filesToAttach = validFiles.slice(0, allowedToAttachMoreFilesCount)
    if (validFiles.length > allowedToAttachMoreFilesCount) {
      addAlert({
        text: translateService.t('common.filesCountError', {
          count: messengerMaxFilesCountPerMessage,
        }),
        scope: alertScope,
      })
    }

    return filesToAttach
  }

  const uploadAttachments = ({ files, uploadApiCallback }: UploadAttachmentsParams) => {
    const validFiles = validateAndFilterFiles(files)
    const promises = validFiles.map((file) => uploadAttachment({ file, uploadApiCallback }))
    return Promise.allSettled(promises)
  }

  const removeAttachment = (id: string) => {
    attachmentStore.removeAttachment(id)
    removeAttachmentAlert(id)
  }

  const retryAttachment = ({ id, uploadApiCallback }: RetryAttachmentParams) => {
    removeAttachmentAlert(id)
    const data = attachmentStore.getAttachmentUploadDataById(id)
    if (data?.file) {
      return uploadAttachment({
        file: data.file,
        id,
        uploadApiCallback,
      })
    }
    return Promise.resolve()
  }

  const setAttachments = (attachments: WidgetAttachment[]) => {
    attachmentStore.setAttachments(attachments)
  }

  const clearAttachments = () => {
    attachmentStore.clearAttachments()
  }

  const getUploadedAttachments = () => {
    return attachmentStore.attachments.value.filter((attachment) => isWidgetUploadedAttachment(attachment))
  }

  const updateProgress = (id: string, progress: number) => {
    attachmentStore.updateUploadingProgress(id, progress)
  }

  return {
    attachments: attachmentStore.attachments,
    uploadAttachments,
    removeAttachment,
    retryAttachment,
    setAttachments,
    clearAttachments,
    getUploadedAttachments,
    updateProgress,
  }
}

const getUploadingAttachmentFromFile = (file: File, id?: string): WidgetUploadingAttachment => {
  return {
    type: 'uploadingAttachment',
    id: id ?? getUuid(),
    name: file.name,
    size: file.size,
    mimeType: file.type,
    uploadProgress: 0,
    previewUrl: isImageMime(file.type) ? URL.createObjectURL(file) : undefined,
  }
}
